import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { Button } from "baseui/button";
import { Check } from "baseui/icon";
import { ListItem, ListItemLabel } from "baseui/list";
import {  ModalButton} from "baseui/modal";
import { useSnackbar } from "baseui/snackbar";
import {  ParagraphXSmall } from "baseui/typography";
import { useState, useEffect } from "react";
import { VscDebugLineByLine } from "react-icons/vsc";
import { IAutomationObject } from "../../../automationObjects";
import { BacnetObjectState, BacnetObjectTypes, BACnetVendorId, MappingTemplate, RunMappingTemplateCommand, useRunMappingTemplateMutation } from "../../../services/mapping/bacnet";
import { ObjectNavigatorModal } from "../../ObjectSelector/Modal";
import { MappingTemplatesView } from "./MappingTemplatesView";
import { Notification } from "baseui/notification";
import { ProgressSteps, Step } from "baseui/progress-steps";
import { AccessRoles, useHasRole } from "../../../hooks/useHasRole";
import { ModalAutoSize } from "../../../components/ModalAutoSize";

export interface MappingTemplatesModalProps {
  edgeDeviceId: string,
  bacnetdeviceid: number,
  vendorId: number,
  selectedItems: BacnetObjectState[],
  resourceGroupId: string
}

export const MappingTemplatesModal = (props: MappingTemplatesModalProps) => {
  const hasAccessMappingWrite = useHasRole(AccessRoles.Mapping.Write);
  const { enqueue } = useSnackbar();
  const [css, theme] = useStyletron();
  const [current, setCurrent] = useState(0);

  const [runTemplate, runTemplateResult] = useRunMappingTemplateMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [showObjectSelector, setShowObjectSelector] = useState(false);
  function onShowObjectSelector() {
    setShowObjectSelector(true);
  }

  const [selectedParent, setSeletctedParent] = useState<IAutomationObject>();
  const onParentSelected = (item: IAutomationObject) => setSeletctedParent(item);

  const [selectedTemplate, setSeletctedTemplate] = useState<MappingTemplate>();

  function onRunTemplate() {
    const cmd: RunMappingTemplateCommand = {
      template: selectedTemplate?.name || '',
      bacnetDeviceId: props.bacnetdeviceid,
      edgeDeviceId: props.edgeDeviceId,
      parentId: selectedParent?.id || '',
      resourceGroupId: selectedParent?.resourceGroupId || '',
      selectedObjects: props.selectedItems?.map((item: BacnetObjectState) => { return { type: item.objectType, instanceId: item.instanceId } })
    }

    runTemplate(cmd);
  }

  useEffect(() => {
    if (runTemplateResult?.isSuccess) {
      enqueue({
        message: 'Template sucessfully executed',
        startEnhancer: ({ size }) => <Check size={size} />,
      });
      setIsOpen(false);
      setCurrent(0);
    }
  }, [runTemplateResult]);

  const canRun = selectedParent && selectedTemplate;
  const canOpen = props.selectedItems.length > 0 && hasAccessMappingWrite;




  const buttonConditions = [
    {
      next: { disabled: !selectedTemplate, onClick: () => setCurrent(1) }
    },
    {
      next: { disabled: !selectedParent, onClick: () => setCurrent(2) },
      prev: { onClick: () => setCurrent(0) }
    }
  ]
  const nextButtonProps = buttonConditions[current]?.next;
  const prevButtonProps = buttonConditions[current]?.prev;


  return (
    <>
      <Button size="compact" disabled={!canOpen} onClick={() => setIsOpen(true)} kind='secondary' startEnhancer={() => <VscDebugLineByLine size={14} />}>
        Templates
      </Button>
      <ModalAutoSize
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        header={'Run Mapping Template'}
        body={<>
          {runTemplateResult.error &&
            <Notification kind='negative'>
              There has been an error: {JSON.stringify(runTemplateResult.error)}
            </Notification>
          }

          <Block width='100%' display='flex' flexDirection='column' >
            <div>

              <SelectedBacnetObjects selectedItems={props.selectedItems} />
            </div>
            <ProgressSteps current={current}>
              <Step title={"Select a Template you want to run (for " + BACnetVendorId[props.vendorId] + ")"}>

                <MappingTemplatesView onTemplateSelected={setSeletctedTemplate} {...props}></MappingTemplatesView>
              </Step>
              <Step title="Select an App as Parent">
                <ParagraphXSmall>Select to parent App where to template should run on</ParagraphXSmall>
                <Button onClick={onShowObjectSelector}>Select Parent</Button>
                {selectedParent &&
                  <ListItem>
                    <ListItemLabel description={selectedParent.description}>
                      {selectedParent.name}
                    </ListItemLabel>
                  </ListItem>
                }
                {!selectedParent &&
                  <Notification kind="warning" >Please select a Parent App</Notification>
                }
                {showObjectSelector &&
                  <ObjectNavigatorModal title='Select App as parent'
                    filter={(app: IAutomationObject) => app.resourceGroupId === props.resourceGroupId}
                    isOpen={showObjectSelector}
                    showAddObject
                    setIsOpen={(e: boolean) => setShowObjectSelector(e)}
                    subTitle={<SelectedBacnetObjects selectedItems={props.selectedItems} />}
                    onSelected={onParentSelected}
                    currentSelectedParent={selectedParent}></ObjectNavigatorModal>
                }
              </Step>
            </ProgressSteps>
          </Block>
        </>}
        footer={<>
          {prevButtonProps && <ModalButton kind="tertiary" {...prevButtonProps}>
            Back
          </ModalButton>}
          {nextButtonProps && <ModalButton kind="secondary" {...nextButtonProps}>
            Next
          </ModalButton>}
          {current === 2 &&
            <>
              <ModalButton kind='tertiary' onClick={() => setIsOpen(false)}>
                Cancel
              </ModalButton>
              <ModalButton isLoading={runTemplateResult.isLoading} disabled={!canRun} onClick={onRunTemplate}>Run</ModalButton>
            </>
          }
        </>}></ModalAutoSize>
    </>
  )
}

const SelectedBacnetObjects = ({ selectedItems }) => {
  if (selectedItems?.length > 1)
    return (<ListItemLabel >
      <b>{selectedItems?.length}</b> BACnet Objects selected
    </ListItemLabel>);

  return (<>
    {selectedItems?.map((bacnetObject: any) => (<ListItem>
      <ListItemLabel description={bacnetObject.objectDescription}>
        {bacnetObject.objectName} - {BacnetObjectTypes[bacnetObject.objectType]}[{bacnetObject.instanceId}]
      </ListItemLabel>
    </ListItem>))}</>);
}